import { Component, Host, Optional, inject, signal } from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSidenav } from '@angular/material/sidenav';
import { Route, Router, RouterModule } from '@angular/router';

import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { MpsPermissionHelper } from '../../helpers/mps-permission.helper';
import { mpsRouteNames } from '../../mps-route-names';
import { MpsUserService } from '../../services/mps-user.service';

@Component({
  selector: 'mps-app-navigation',
  templateUrl: './mps-navigation.component.html',
  styleUrls: ['./mps-navigation.component.scss'],
  imports: [TranslateModule, MatMenuModule, MatIconModule, MatDividerModule, RouterModule],
})
export class MpsNavigationComponent {
  private readonly _routes: Route[] = [];
  private _userService = inject(MpsUserService);
  private _router = inject(Router);
  private _translateService = inject(TranslateService);
  private _dateAdapter = inject<DateAdapter<unknown>>(DateAdapter);

  user = signal(this._userService.getUserSubject().getValue());
  logoutRoute = mpsRouteNames.auth + '/' + mpsRouteNames.logout;
  languages = ['fr', 'en'];
  expandedRoutes = new Set<string>();

  constructor(@Host() @Optional() private _sidenav: MatSidenav) {
    this._routes = this._router.config;
  }

  public getRoutes(): Route[] {
    return this._routes;
  }

  public close(): void {
    if (this._sidenav) {
      this._sidenav.close();
    }
  }

  toggleExpand(path: string): void {
    if (this.expandedRoutes.has(path)) {
      this.expandedRoutes.delete(path);
    } else {
      this.expandedRoutes.add(path);
    }
  }

  isExpanded(path: string): boolean {
    return this.expandedRoutes.has(path);
  }

  isMenuItemDisplayed(route: Route): boolean {
    const user = this._userService.getUserSubject().getValue();
    if (!user) {
      return false;
    }

    const hasTitle = route.data?.['title'];
    const canUserActivate = MpsPermissionHelper.canUserActivate(user, route);

    return hasTitle && canUserActivate;
  }

  isChildRouteActive(route: Route): boolean {
    if (!route || !route.children) {
      return false;
    }
    return route.children.some((child: Route) =>
      this._router.isActive(this._router.createUrlTree([route.path, child.path]), {
        paths: 'subset',
        queryParams: 'ignored',
        fragment: 'ignored',
        matrixParams: 'ignored',
      })
    );
  }

  getUserInitials(): string {
    const userData = this.user();
    if (!userData) return '';
    const firstInitial = userData.firstname?.charAt(0)?.toUpperCase() || '';
    const lastInitial = userData.lastname?.charAt(0)?.toUpperCase() || '';
    return firstInitial + lastInitial;
  }

  getUserFullName(): string {
    const userData = this.user();
    if (!userData) return '';
    return `${userData.firstname} ${userData.lastname}`;
  }

  getUserEmail(): string {
    return this.user()?.email || '';
  }

  get currentLang(): string {
    return this._translateService.currentLang;
  }

  getLangLabel(lang: string): string {
    const labels: Record<string, string> = {
      fr: 'FR',
      en: 'EN',
    };
    return labels[lang] || lang.toUpperCase();
  }

  switchLanguage(lang: string): void {
    this._translateService.use(lang);
    this._dateAdapter.setLocale(lang);
    const user = this._userService.getUser();
    localStorage.setItem('lang', lang);
    if (user) {
      this._userService.changeLang(user.id, lang)?.subscribe();
    }
  }
}
